<template>
  <v-card color="white" :loading="loading" :disabled="loading">
    <v-card-title class="justify-center" style="background-color: #5E676E;">
      <div class="white--text">
        <div class="text-center">細節設定</div>
        <div class="text-center text-body-2">（下滑設定更多內容）</div>
      </div>
    </v-card-title>

    <v-card-text class="py-8 overflow-y-auto d-flex flex-column" :style="cardTextStyle">
      <div v-show="canShow([2, 3], clientPermissions, modeSetting)">
        <div
          class="bullet text-subtitle-1 black--text"
          v-html="$t('FreeTimeSetting.whetherOpenOnline')"
        />
        <v-row no-gutters dense style="gap: 0 10px">
          <v-col cols="12" sm="auto" md="auto">
            <v-checkbox
              :input-value="detailSetting.current_appointment_status"
              true-value="1"
              false-value="0"
              hide-details
              dense
              class="ma-0"
              @change="save('current_appointment_status', $event)"
            >
              <template v-slot:label>
                <div v-html="$t('FreeTimeSetting.openCurrentDay')"/>
              </template>
            </v-checkbox>
          </v-col>
          <v-col cols="12" sm="auto" md="auto">
            <v-checkbox
              :input-value="detailSetting.tomorrow_appointment_status"
              true-value="1"
              false-value="0"
              hide-details
              dense
              class="ma-0"
              @change="save('tomorrow_appointment_status', $event)"
            >
              <template v-slot:label>
                <div v-html="$t('FreeTimeSetting.openTomorrow')"/>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>
      </div>

      <div v-show="canShow([2, 3], clientPermissions, modeSetting)">
        <div class="bullet text-subtitle-1 black--text">
          {{ $t('FreeTimeSetting.freeTimeGapMode') }}
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">
                mdi-help-circle-outline
              </v-icon>
            </template>
            {{ $t('FreeTimeSetting.freeTimeGapTooltip') }}
          </v-tooltip>
        </div>
        <v-row no-gutters dense style="gap: 0 10px">
          <v-col cols="12" sm="auto" md="auto">
            <v-radio-group
              row
              class="mt-0"
              hide-details
              :value="detailSetting.free_time_gap"
              @change="save('free_time_gap', $event)"
            >
              <v-radio
                :label="$t('FreeTimeSetting.thirtyMinutes')"
                value="1"
              />
              <v-radio
                :label="$t('FreeTimeSetting.fifteenMinutes')"
                value="2"
              />
              <v-radio
                value="3"
              >
                <template v-slot:label>
                  自訂(
                  <router-link :to="{name: 'treatmentSetting', query: {code: settingCode}}" class="link_blue--text">
                    <span class="text-decoration-underline">前往設定頁面</span>
                  </router-link>
                  )
                </template>
              </v-radio>
            </v-radio-group>
          </v-col>
        </v-row>
      </div>

      <div v-show="canShow([1, 2, 3], clientPermissions, modeSetting)">
        <div class="bullet text-subtitle-1 black--text">
          預約注意事項
          <v-btn icon small :disabled="!bookingNoteReady || bookingNoteSaving" aria-label="編輯預約注意事項" @click="openBookingNoteEditor">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-dialog v-model="editBookingNoteDialog" max-width="400" :persistent="bookingNoteSaving">
            <v-card class="booking-note-dialog">
              <v-card-title class="booking-note-text">編輯預約注意事項</v-card-title>
              <v-card-text>
                <v-textarea
                  v-model="editBookingNoteContent"
                  label="自訂預約注意事項"
                  hide-details
                  outlined
                  rows="4"
                  no-resize
                  full-width
                  clearable
                  :disabled="bookingNoteSaving"
                />
                <div class="mt-3 booking-note-text">
                  <div>留空時患者端會顯示以下公版內容</div>
                  <div v-if="bookingNoteDefaultStatus === 'ready'" class="text-pre-line">{{ bookingNoteDefault }}</div>
                  <div v-else-if="bookingNoteDefaultStatus === 'error'" role="status">
                    公版內容暫時無法載入
                    <v-btn small text color="primary" @click="loadBookingNoteDefault">重試</v-btn>
                  </div>
                  <div v-else role="status">公版內容載入中…</div>
                </div>
                <div v-if="bookingNoteSaveError" class="mt-3 error--text booking-note-text" role="alert">{{ bookingNoteSaveError }}</div>
              </v-card-text>
              <v-card-actions>
                <v-spacer/>
                <v-btn color="primary" :loading="bookingNoteSaving" :disabled="bookingNoteSaving" @click="saveBookingNote">確認</v-btn>
                <v-btn color="grey-4" :disabled="bookingNoteSaving" @click="editBookingNoteDialog = false">取消</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
        <v-sheet outlined class="pa-2 text--darken-1 grey--text booking-note-text" aria-live="polite">
          <template v-if="bookingNoteReady">
            <template v-if="!bookingNoteIsDefault || bookingNoteDefaultStatus === 'ready'">
              <div v-if="bookingNoteIsDefault" class="text-caption mb-1">系統公版預設內容</div>
              <div class="text-pre-line">{{ effectiveBookingNote }}</div>
            </template>
            <div v-else-if="bookingNoteDefaultStatus === 'error'">
              公版內容暫時無法載入
              <v-btn small text color="primary" @click="loadBookingNoteDefault">重試</v-btn>
            </div>
            <div v-else>公版內容載入中…</div>
          </template>
          <div v-else-if="bookingNoteRawError">
            預約注意事項暫時無法載入
            <v-btn small text color="primary" @click="loadBookingNoteDetail">重試</v-btn>
          </div>
          <div v-else>預約注意事項載入中…</div>
        </v-sheet>
      </div>

      <div v-show="canShow([1, 2, 3], clientPermissions, modeSetting)">
        <div class="bullet text-subtitle-1 black--text">
          備註欄提示文字
          <v-btn icon small :disabled="!notePlaceholderReady || notePlaceholderSaving" aria-label="編輯備註欄提示文字" @click="openNotePlaceholderEditor">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-dialog v-model="editNotePlaceholderDialog" max-width="400" :persistent="notePlaceholderSaving">
            <v-card class="booking-note-dialog">
              <v-card-title class="booking-note-text">編輯備註欄提示文字</v-card-title>
              <v-card-text>
                <v-textarea
                  v-model="editNotePlaceholderContent"
                  label="自訂備註欄提示文字"
                  hide-details
                  outlined
                  rows="3"
                  no-resize
                  full-width
                  :disabled="notePlaceholderSaving"
                />
                <div class="text-right text-caption mt-1" :class="{'error--text': notePlaceholderTooLong}" aria-live="polite">
                  {{ notePlaceholderCount }} / {{ NOTE_PLACEHOLDER_MAX }}
                </div>
                <div class="mt-3 booking-note-text">
                  <div>留空時患者端會顯示以下公版內容</div>
                  <div v-if="notePlaceholderDefaultStatus === 'ready'" class="text-pre-line">{{ notePlaceholderDefault }}</div>
                  <div v-else-if="notePlaceholderDefaultStatus === 'error'" role="status">
                    公版內容暫時無法載入
                    <v-btn small text color="primary" @click="loadNotePlaceholderDefault">重試</v-btn>
                  </div>
                  <div v-else role="status">公版內容載入中…</div>
                </div>
                <div v-if="notePlaceholderSaveError" class="mt-3 error--text booking-note-text" role="alert">{{ notePlaceholderSaveError }}</div>
              </v-card-text>
              <v-card-actions>
                <v-spacer/>
                <v-btn color="primary" :loading="notePlaceholderSaving" :disabled="notePlaceholderTooLong || notePlaceholderSaving" @click="saveNotePlaceholder">確認</v-btn>
                <v-btn color="grey-4" :disabled="notePlaceholderSaving" @click="editNotePlaceholderDialog = false">取消</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
        <v-sheet outlined class="pa-2 booking-note-text" :class="notePlaceholderIsDefault ? 'grey--text' : 'text--darken-1 grey--text'" aria-live="polite">
          <template v-if="notePlaceholderReady">
            <div v-if="!notePlaceholderIsDefault || notePlaceholderDefaultStatus === 'ready'" class="text-pre-line">{{ effectiveNotePlaceholder }}</div>
            <div v-else-if="notePlaceholderDefaultStatus === 'error'">
              公版內容暫時無法載入
              <v-btn small text color="primary" @click="loadNotePlaceholderDefault">重試</v-btn>
            </div>
            <div v-else>公版內容載入中…</div>
          </template>
          <div v-else-if="notePlaceholderRawError">
            備註欄提示文字暫時無法載入
            <v-btn small text color="primary" @click="loadBookingNoteDetail">重試</v-btn>
          </div>
          <div v-else>備註欄提示文字載入中…</div>
        </v-sheet>
      </div>

      <div v-show="canShow([1, 2, 3], clientPermissions, modeSetting)">
        <div class="bullet text-subtitle-1 black--text">
          預約模式
        </div>
        <div class="d-flex flex-wrap">
          <div v-show="canShow([3], clientPermissions, modeSetting)">
            <v-checkbox
              :input-value="detailSetting.smart_mode_status"
              true-value="1"
              false-value="0"
              hide-details
              dense
              class="ma-0"
              @change="save('smart_mode_status', $event)"
            >
              <template v-slot:label>
                {{ $t('FreeTimeSetting.smartMode') }}
              </template>
              <template v-slot:append>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  {{ $t('FreeTimeSetting.smartTooltip') }}
                </v-tooltip>
              </template>
            </v-checkbox>
          </div>
          <div>
            <v-checkbox
              :input-value="detailSetting.single_unprocessed_appointment_status"
              true-value="1"
              false-value="0"
              hide-details
              dense
              class="ma-0"
              label="限制患者只能預約此位醫師一筆線上預約"
              @change="handleSingleUnprocessedChange"
            />
            <div
              v-if="clinicUser"
              class="d-flex align-center mt-2"
              style="background-color: #FFF8E1; border: 1px solid #FFE082; border-radius: 6px; padding: 8px 12px; gap: 6px;"
            >
              <v-icon size="16" color="#F9A825">mdi-arrow-up</v-icon>
              <span style="color: #F57F17; font-size: 12px;">
                如需限制患者在全院僅能送出一筆預約，請至<a
                  style="color: #F57F17; cursor: pointer; text-decoration: underline;"
                  @click="$emit('navigate-clinic-wide')"
                >「全院預約限制」</a>設定
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-show="canShow([2, 3], clientPermissions, modeSetting)">
        <div class="bullet text-subtitle-1 black--text">
          患者可預約的日期範圍
        </div>
        <div class="d-flex  align-center" style="gap: 4px;">
          <div>開放</div>
          <v-select
            v-if="editMonthCount"
            v-model="monthsCount"
            hide-details
            dense
            outlined
            style="max-width: 80px;"
            :items="[1, 2, 3, 4, 5, 6, 7]"
          />
          <div v-else class="text-decoration-underline">{{ detailSetting.months_count }}</div>
          <div>個月內的可預約時段</div>
          <v-btn
            v-if="editMonthCount"
            color="red-6"
            dark
            small
            class="ml-2"
            @click="save('months_count', monthsCount)"
          >確認儲存
          </v-btn>
          <v-btn
            v-else
            icon
            @click="monthsCount = detailSetting.months_count; editMonthCount = true"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </div>
      </div>

      <div v-show="canShow([-100.1, -100.2, -100.9], clientPermissions, modeSetting)">
        <div
          class="bullet text-subtitle-1 black--text"
        >
          加入LINE預約模式選擇
        </div>
        <v-radio-group
          v-model="guideToLineMode"
          class="mt-0"
          hide-details
          @change="checkHasChannel('guideToLine', $event, handleRadioChange)"
        >
          <div class="d-flex" :class="{'flex-wrap': $vuetify.breakpoint.xsOnly}" style="gap: 8px;">
            <v-radio
              label="請患者加入「診所」LINE"
              :value="1"
              class="my-0"
            />
            <v-radio
              label="請患者加入「醫師」LINE"
              :value="2"
              class="my-0"
            />
          </div>
          <div
            class="d-flex align-center mt-2"
            :class="{'flex-wrap': $vuetify.breakpoint.xsOnly}"
            style="gap: 8px;"
          >
            <v-radio
              label="自行輸入預約連結"
              :value="3"
              class="my-0"
            />
            <v-text-field
              ref="textReservePageFieldRef"
              :value="detailSetting.text_reserve_page"
              dense
              outlined
              :disabled="guideToLineMode !== 3"
              append-outer-icon="mdi-content-save-outline"
              hide-details
              style=" max-width: 350px;"
              :class="{'pl-6': $vuetify.breakpoint.xsOnly}"
              @click:append-outer="save('text_reserve_page', $refs.textReservePageFieldRef.internalValue)"
            />
          </div>
        </v-radio-group>
      </div>

      <div v-show="canShow([1, 2, 3], clientPermissions, modeSetting)">
        <div
          class="bullet text-subtitle-1 black--text"
        >
          引導加LINE
        </div>
        <v-radio-group
          :value="Number(detailSetting.appointment_completed_line_page_kind)"
          class="mt-0"
          hide-details
          @change="checkHasChannel('appointmentCompleted', $event, saveAppointmentCompletedLinePageKind)"
        >
          <div class="d-flex" :class="{'flex-wrap': $vuetify.breakpoint.xsOnly}" style="gap: 8px;">
            <v-radio
              label="引導加入「診所」LINE"
              :value="1"
              class="my-0"
            />
            <v-radio
              label="引導加入「醫師」LINE"
              :value="2"
              class="my-0"
            />
          </div>
          <div
            class="d-flex align-center mt-2"
            :class="{'flex-wrap': $vuetify.breakpoint.xsOnly}"
            style="gap: 8px;"
          >
            <v-radio
              label="自行輸入預約連結"
              :value="3"
              class="my-0"
            />
            <v-text-field
              ref="appointmentCompletedLinePageLinkFieldRef"
              :value="detailSetting.appointment_completed_line_page_link"
              dense
              outlined
              :disabled="Number(detailSetting.appointment_completed_line_page_kind) !== 3"
              append-outer-icon="mdi-content-save-outline"
              hide-details
              style=" max-width: 350px;"
              :class="{'pl-6': $vuetify.breakpoint.xsOnly}"
              @click:append-outer="save('appointment_completed_line_page_link', $refs.appointmentCompletedLinePageLinkFieldRef.internalValue)"
            />
          </div>
        </v-radio-group>
      </div>

      <v-dialog v-model="checkChannelDialog" max-width="300">
        <v-card>
          <v-card-title>
            <div>
              您尚未串接LINE官方帳號，請洽 <a :href="$enum.DENTCO_CS_LINE" target="_blank" class="link_blue--text">客服人員</a>
            </div>
          </v-card-title>
          <v-card-actions>
            <v-btn block color="primary" @click="checkChannelDialog = false">確認</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <ConfirmDialog
        v-model="checkTimeGapDialog"
        title="請您再次確認"
        :content="checkTimeGapContent"
        @confirm="confirmTimeGap"
      />

      <ConfirmDialog
        v-model="showTimeGapHintDialog"
        title=""
        content=""
        confirm-btn-text="前往編輯空檔時間長度"
        @confirm="confirmTimeGapHint"
      />

      <ConfirmDialog
        v-model="clinicWideConfirmDialog"
        title="請您再次確認"
        content="取消此醫師的預約限制，將同時關閉「全院僅能預約一筆」的設定。確定要繼續嗎？"
        @confirm="confirmClinicWideOff"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { updateFreeTimeDetailAPI } from "../../api";
import { canShow } from "../../utils/tool";
import { getAppointmentDefaultBookingNote, getAppointmentDefaultNotePlaceholder } from "@/api/appointment-defaults";
import { bookingNoteScope, isUncustomizedBookingNote } from "@/utils/booking-note";
import { NOTE_PLACEHOLDER_MAX, normalizeNotePlaceholder, countNotePlaceholderChars } from "@/utils/note-placeholder";
import ConfirmDialog from "@/components/utils/ConfirmDialog.vue";

export default {
  name: "SettingDetail",
  components: {ConfirmDialog},

  props: {
    clientPermissions: {
      type: Array,
      required: true
    },
    settingCode: {
      type: String,
      required: true
    },
    cardTextStyle: {
      type: String,
      required: false
    }
  },

  data() {
    return {
      loading: false,
      editBookingNoteDialog: false,
      editBookingNoteContent: null,
      bookingNoteDefault: '',
      bookingNoteDefaultStatus: 'idle',
      bookingNoteEditorScope: null,
      bookingNoteEpoch: 0,
      bookingNoteSaving: false,
      bookingNoteSaveError: '',
      bookingNoteDisposed: false,
      NOTE_PLACEHOLDER_MAX,
      editNotePlaceholderDialog: false,
      editNotePlaceholderContent: '',
      notePlaceholderDefault: '',
      notePlaceholderDefaultStatus: 'idle',
      notePlaceholderEditorScope: null,
      notePlaceholderSaving: false,
      notePlaceholderSaveError: '',
      monthsCount: null,
      editMonthCount: false,
      guideToLineMode: null,
      guideToLineCondition: [-100.1, -100.2, -100.9],
      canShow,
      checkChannelDialog: false,

      checkTimeGapDialog: false,
      checkTimeGapContent: '',
      switchTTimeGapType: null,
      showTimeGapHintDialog: false,
      clinicWideConfirmDialog: false
    }
  },

  computed: {
    ...mapGetters([
      'clinicUser',
      'axiosParamData',
      'modeSetting',
      'detailSetting',
      'detailSettingRequest'
    ]),
    bookingNoteParams() {
      const params = {...this.axiosParamData}
      params[this.clinicUser ? 'doctor_code' : 'clinic_code'] = this.settingCode
      return params
    },
    bookingNoteScope() {
      return bookingNoteScope(this.bookingNoteParams)
    },
    bookingNoteReady() {
      const request = this.detailSettingRequest
      return request.status === 'ready' && request.scope === this.bookingNoteScope &&
        (this.detailSetting.booking_note === null || typeof this.detailSetting.booking_note === 'string')
    },
    bookingNoteRawError() {
      return this.detailSettingRequest.scope === this.bookingNoteScope && this.detailSettingRequest.status === 'error'
    },
    bookingNoteIsDefault() {
      return this.bookingNoteReady && isUncustomizedBookingNote(this.detailSetting.booking_note)
    },
    effectiveBookingNote() {
      if (!this.bookingNoteReady) return ''
      return this.bookingNoteIsDefault ? this.bookingNoteDefault : this.detailSetting.booking_note
    },
    notePlaceholderReady() {
      return this.bookingNoteReady && typeof this.detailSetting.note_placeholder === 'string'
    },
    notePlaceholderRawError() {
      return this.bookingNoteRawError || (this.bookingNoteReady && !this.notePlaceholderReady)
    },
    notePlaceholderIsDefault() {
      return this.notePlaceholderReady && normalizeNotePlaceholder(this.detailSetting.note_placeholder) === ''
    },
    effectiveNotePlaceholder() {
      if (!this.notePlaceholderReady) return ''
      return this.notePlaceholderIsDefault ? this.notePlaceholderDefault : this.detailSetting.note_placeholder
    },
    notePlaceholderCount() {
      return countNotePlaceholderChars(this.editNotePlaceholderContent)
    },
    notePlaceholderTooLong() {
      return this.notePlaceholderCount > NOTE_PLACEHOLDER_MAX
    },
    timeGapMapping() {
      return {
        '1': 30,
        '2': 15,
        '3': '自定義'
      }
    }
  },

  watch: {
    bookingNoteScope: {
      handler() {
        this.bookingNoteEpoch++
        this.editBookingNoteDialog = false
        this.bookingNoteEditorScope = null
        this.bookingNoteSaving = false
        this.bookingNoteSaveError = ''
        this.editNotePlaceholderDialog = false
        this.notePlaceholderEditorScope = null
        this.notePlaceholderSaving = false
        this.notePlaceholderSaveError = ''
        return this.loadBookingNoteDetail()
      },
      immediate: true,
    },
    modeSetting: {
      handler() {
        this.setGuideToLineMode();
      },
      immediate: true,
      deep: true
    }
  },

  mounted() {
    this.loadBookingNoteDefault()
    this.loadNotePlaceholderDefault()
  },

  beforeDestroy() {
    this.bookingNoteDisposed = true
    this.bookingNoteEpoch++
  },

  methods: {
    ...mapActions({
      getFreeTimeDetail: 'getFreeTimeDetail',
      getFreeTimeMode: 'getFreeTimeMode',
    }),
    async loadBookingNoteDefault() {
      if (this.bookingNoteDefaultStatus === 'loading') return
      this.bookingNoteDefaultStatus = 'loading'
      try {
        const note = await getAppointmentDefaultBookingNote()
        if (this.bookingNoteDisposed) return
        this.bookingNoteDefault = note
        this.bookingNoteDefaultStatus = 'ready'
      } catch {
        if (!this.bookingNoteDisposed) this.bookingNoteDefaultStatus = 'error'
      }
    },

    async loadNotePlaceholderDefault() {
      if (this.notePlaceholderDefaultStatus === 'loading') return
      this.notePlaceholderDefaultStatus = 'loading'
      try {
        const note = await getAppointmentDefaultNotePlaceholder()
        if (this.bookingNoteDisposed) return
        this.notePlaceholderDefault = note
        this.notePlaceholderDefaultStatus = 'ready'
      } catch {
        if (!this.bookingNoteDisposed) this.notePlaceholderDefaultStatus = 'error'
      }
    },

    async loadBookingNoteDetail() {
      try {
        return await this.getFreeTimeDetail({...this.bookingNoteParams})
      } catch {
        // The store exposes a scoped error and the notice offers an explicit retry.
        return null
      }
    },

    openBookingNoteEditor() {
      if (!this.bookingNoteReady || this.bookingNoteSaving) return
      this.bookingNoteEditorScope = this.bookingNoteScope
      this.editBookingNoteContent = this.bookingNoteIsDefault ? '' : this.detailSetting.booking_note
      this.bookingNoteSaveError = ''
      this.editBookingNoteDialog = true
    },

    openNotePlaceholderEditor() {
      if (!this.notePlaceholderReady || this.notePlaceholderSaving) return
      this.notePlaceholderEditorScope = this.bookingNoteScope
      this.editNotePlaceholderContent = this.notePlaceholderIsDefault ? '' : this.detailSetting.note_placeholder
      this.notePlaceholderSaveError = ''
      this.editNotePlaceholderDialog = true
    },

    async saveNotePlaceholder() {
      if (this.notePlaceholderSaving || this.notePlaceholderTooLong || !this.editNotePlaceholderDialog ||
        this.notePlaceholderEditorScope !== this.bookingNoteScope) return
      const scope = this.bookingNoteScope
      const epoch = this.bookingNoteEpoch
      const params = {...this.bookingNoteParams}
      const value = normalizeNotePlaceholder(this.editNotePlaceholderContent)
      const isCurrent = () => !this.bookingNoteDisposed && epoch === this.bookingNoteEpoch && scope === this.bookingNoteScope
      this.notePlaceholderSaving = true
      this.notePlaceholderSaveError = ''
      let saved = false
      try {
        await updateFreeTimeDetailAPI({...params, note_placeholder: value})
        saved = true
        if (!isCurrent()) return
        const detail = await this.getFreeTimeDetail(params)
        if (!isCurrent()) return
        if (!detail || detail.note_placeholder !== value) throw new Error('Unconfirmed readback')
        this.editNotePlaceholderDialog = false
      } catch (error) {
        if (isCurrent()) {
          this.notePlaceholderSaveError = saved ? '儲存結果尚未確認，請重試或重新載入。' :
            (error.response && error.response.status === 400 ? '儲存失敗：內容超過 14 字或格式不正確。' : '儲存失敗，請稍後再試。')
        }
      } finally {
        if (isCurrent()) this.notePlaceholderSaving = false
      }
    },

    async saveBookingNote() {
      if (this.bookingNoteSaving || !this.editBookingNoteDialog || this.bookingNoteEditorScope !== this.bookingNoteScope) return
      const scope = this.bookingNoteScope
      const epoch = this.bookingNoteEpoch
      const params = {...this.bookingNoteParams}
      const isCurrent = () => !this.bookingNoteDisposed && epoch === this.bookingNoteEpoch && scope === this.bookingNoteScope
      this.bookingNoteSaving = true
      this.bookingNoteSaveError = ''
      let saved = false
      try {
        await updateFreeTimeDetailAPI({...params, booking_note: this.editBookingNoteContent})
        saved = true
        if (!isCurrent()) return
        const detail = await this.getFreeTimeDetail(params)
        if (!isCurrent()) return
        if (!detail) throw new Error('Unconfirmed readback')
        this.editBookingNoteDialog = false
      } catch {
        if (isCurrent()) this.bookingNoteSaveError = saved ? '儲存結果尚未確認，請重試或重新載入。' : '儲存失敗，請稍後再試。'
      } finally {
        if (isCurrent()) this.bookingNoteSaving = false
      }
    },

    async save(key, value, refreshMode = false, skipCheck=false) {

      if (!skipCheck) {  // 各種 78 check 都放這裡
        switch (key) {
          case 'free_time_gap':
            if (this.detailSetting.free_time_gap === '3') {
              this.switchTTimeGapType = value
              this.checkTimeGapContent = `此操作會將所有治療項目時間重設為${this.timeGapMapping[value]}分鐘，是否確定繼續？`
              this.checkTimeGapDialog = true

              this.$nextTick(() => {
                const tmp = this.detailSetting.free_time_gap
                this.detailSetting.free_time_gap = value

                this.$nextTick(() => {
                  this.detailSetting.free_time_gap = tmp
                });
              });
              return
            }
        }
      }

      const data = {
        ...this.axiosParamData
      }
      data[key] = value
      if (this.clinicUser)
        data.doctor_code = this.settingCode
      else
        data.clinic_code = this.settingCode

      this.loading = true
      return updateFreeTimeDetailAPI(data).then(res => {
        this.editBookingNoteDialog = false
        this.editMonthCount = false

        const params = {
          ...this.axiosParamData
        }
        if (this.clinicUser)
          params.doctor_code = this.settingCode
        else
          params.clinic_code = this.settingCode

        this.$nextTick(async () => {
          this.loading = true
          if (refreshMode) await this.getFreeTimeMode(params)
          await this.getFreeTimeDetail(params)
          this.loading = false
        })

        if (key === 'free_time_gap' && value === '3') {
          this.showTimeGapHintDialog = true
        }
      }).catch(e => {
        console.error(e)
        this.$notify.error('儲存失敗')
        this.loading = false
      }).finally(() => {
      })
    },

    handleSingleUnprocessedChange(value) {
      if (value === '0' && this.detailSetting.clinic_wide_unprocessed_appointment_status === '1') {
        this.clinicWideConfirmDialog = true
        return
      }
      this.save('single_unprocessed_appointment_status', value)
    },

    confirmClinicWideOff({ callback }) {
      this.save('single_unprocessed_appointment_status', '0')
      callback && callback()
    },

    handleRadioChange(newValue) {
      this.guideToLineMode = newValue

      this.save('direct_message_channel', newValue, true).then(() => {
      }).catch(error => {
        // 處理保存失敗的情況
        console.error("保存失敗：", error);
        this.setGuideToLineMode();
      }).finally(() => {
        // this.setGuideToLineMode();
      })
    },

    setGuideToLineMode() {
      function filterGuideToLineCondition(modeSetting, guideToLineCondition) {
        // 定義需要檢查的狀態鍵
        const keysToCheck = ["line_appointment_status", "web_appointment_status"];

        // 過濾出匹配 guideToLineCondition 的值
        const filteredValues = keysToCheck
          .filter(key => guideToLineCondition.includes(Number(modeSetting[key])))
          .map(key => Number(modeSetting[key]));

        if (filteredValues.length) {
          return filteredValues[0]
        }

        return null
      }

      const mode = filterGuideToLineCondition(this.modeSetting, this.guideToLineCondition)
      switch (mode) {
        case -100.1:
          this.guideToLineMode = 1
          break
        case -100.2:
          this.guideToLineMode = 2
          break
        case -100.9:
          this.guideToLineMode = 3
          break
        default:
          this.guideToLineMode = null
          break
      }
    },

    saveAppointmentCompletedLinePageKind(value) {
      this.save('appointment_completed_line_page_kind', value)
    },

    checkHasChannel(key, event, action) {
      const directMessageChannelMapping = {
        1: 'clinic_channel',
        2: 'doctor_channel'
      }
      if (key === 'appointmentCompleted') {
        delete directMessageChannelMapping[1]
      }
      if (!Object.keys(directMessageChannelMapping).includes(String(event)) || this.detailSetting[directMessageChannelMapping[event]]) {
        action(event);
      } else {
        this.checkChannelDialog = true
        this.$nextTick(() => {
          if (key === 'guideToLine')
            this.setGuideToLineMode()
          if (key === 'appointmentCompleted') {
            const tmp = Number(this.detailSetting.appointment_completed_line_page_kind)
            this.detailSetting.appointment_completed_line_page_kind = event
            this.$nextTick(() => {
              this.detailSetting.appointment_completed_line_page_kind = tmp
            })
          }
        })
      }
    },
    confirmTimeGap(payload) {
      this.save('free_time_gap', this.switchTTimeGapType, false, true)
      const {callback} = payload;
      callback && callback()
    },
    confirmTimeGapHint(payload) {
      this.$router.push({name: 'treatmentSetting', query: {code: this.settingCode}})
      const {callback} = payload;
      callback && callback()
    }
  }
}
</script>

<style scoped>
.booking-note-text {
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  white-space: pre-line;
}
.booking-note-dialog .v-card__title {
  word-break: normal;
}
</style>
